import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { getStoredRoleHomePath, isAuthenticated as getIsAuthenticated, normalizeStoredRole, roleStorageKey } from './auth'
import { useAuthStore } from '../features/auth/hooks/useAuthStore'
import { getAccessToken } from '../shared/utils/tokenStorage'

const ROUTE_PERMISSION_MAP: Array<{ prefix: string; permissions: string[] }> = [
  // User, Role, Department & Designation Management
  { prefix: '/admin/user-management', permissions: ['user-management.view', 'user-management', 'users.view', 'roles.view'] },
  { prefix: '/admin/department', permissions: ['departments.view', 'department.view', 'hr.view', 'user-management'] },
  { prefix: '/admin/designation', permissions: ['designations.view', 'designation.view', 'hr.view', 'user-management'] },
  { prefix: '/admin/role-management', permissions: ['roles.view', 'role-management.view', 'user-management'] },

  // Finance & Fees
  { prefix: '/admin/finance', permissions: ['finance.view', 'finance'] },
  { prefix: '/finance', permissions: ['finance.view', 'finance'] },
  { prefix: '/admin/fees-finance', permissions: ['finance.view', 'finance'] },
  { prefix: '/fees-finance', permissions: ['finance.view', 'finance'] },
  { prefix: '/admin/fee-head', permissions: ['finance.view', 'finance'] },
  { prefix: '/admin/fee-structure', permissions: ['finance.view', 'finance'] },

  // Admissions & Verification
  { prefix: '/admin/admission', permissions: ['admissions.view', 'admissions'] },
  { prefix: '/admin/interview', permissions: ['admissions.view', 'admissions'] },
  { prefix: '/admin/document-verification', permissions: ['admissions.view', 'document-verification.view', 'admissions'] },
  { prefix: '/admin/enrollment', permissions: ['admissions.view', 'admissions', 'seat-management.view', 'seat-management:view'] },
  { prefix: '/admin/track-candidates', permissions: ['admissions.view', 'admissions'] },
  { prefix: '/admin/candidate-remarks', permissions: ['admissions.view', 'admissions'] },

  // Academics, Classes, Seat Management & Timetable
  { prefix: '/admin/academics', permissions: ['academics.view', 'academics'] },
  { prefix: '/admin/class-management', permissions: ['class-management.view', 'classes.view', 'grades.view', 'academics'] },
  { prefix: '/admin/seat-management', permissions: ['seat-management.view', 'seat-management:view', 'admissions.view', 'admissions'] },
  { prefix: '/admin/add-class', permissions: ['class-management.view', 'grades.view', 'academics'] },
  { prefix: '/admin/add-section', permissions: ['class-management.view', 'sections.view', 'academics'] },
  { prefix: '/admin/set-criteria', permissions: ['class-management.view', 'admissions'] },

  // Examinations
  { prefix: '/admin/academic/examinations', permissions: ['examinations.view', 'examinations'] },
  { prefix: '/admin/examinations', permissions: ['examinations.view', 'examinations'] },
  { prefix: '/admin/examination', permissions: ['examinations.view', 'examinations'] },

  // Front Office & Directories
  { prefix: '/admin/frontoffice/staff-directory', permissions: ['staff-directory.view', 'teachers.view', 'hr'] },
  { prefix: '/admin/frontoffice/staff', permissions: ['staff-directory.view', 'teachers.view', 'hr'] },
  { prefix: '/admin/student-directory', permissions: ['student-directory.view', 'students.view'] },
  { prefix: '/admin/frontoffice/student-directory', permissions: ['student-directory.view', 'students.view'] },
  { prefix: '/admin/frontoffice/student', permissions: ['student-directory.view', 'students.view'] },

  // HR & Staff
  { prefix: '/admin/hr-management', permissions: ['hr.view', 'hr'] },
  { prefix: '/admin/hr', permissions: ['hr.view', 'hr'] },

  // Communication
  { prefix: '/admin/communication', permissions: ['communication.view', 'communications.view', 'communication'] },

  // Transport
  { prefix: '/admin/transport', permissions: ['transport.view', 'transport'] },

  // Library
  { prefix: '/admin/library', permissions: ['library.view', 'library'] },

  // Alumni
  { prefix: '/admin/alumni', permissions: ['alumni.view', 'alumni'] },
  { prefix: '/admin/alumini', permissions: ['alumni.view', 'alumni'] },

  // Events & Calendar
  { prefix: '/admin/events-calendar', permissions: ['academics.view', 'academics:view', 'academics', 'events.view', 'events'] },
  { prefix: '/admin/events', permissions: ['academics.view', 'academics:view', 'academics', 'events.view', 'events'] },
  { prefix: '/admin/calendar', permissions: ['academics.view', 'academics:view', 'academics', 'events.view', 'events'] },

  // Settings & Profile
  { prefix: '/admin/settings', permissions: ['settings.view', 'settings'] },
  { prefix: '/admin/adminsettings', permissions: ['settings.view', 'settings'] },
  { prefix: '/admin/events-dashboard', permissions: ['events.view', 'events'] },
]

export function ProtectedRoute() {
  const location = useLocation()
  const storeAuth = useAuthStore()

  const token = getAccessToken()
  const isAuthenticated = Boolean(storeAuth.isAuthenticated || (token && getIsAuthenticated()))

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  // Get active user role from store or localStorage
  const rawRole = storeAuth.role || window.localStorage.getItem(roleStorageKey)
  const normalizedRole = normalizeStoredRole(rawRole) || rawRole
  const isAdminRole =
    normalizedRole === 'admin' ||
    normalizedRole === 'superadmin' ||
    normalizedRole === 'branch_admin' ||
    normalizedRole === 'branchadmin' ||
    normalizedRole === 'organizational_admin' ||
    normalizedRole === 'org_admin'

  const path = location.pathname

  // Settings and Profile routes do not require any permission check for authenticated users
  if (path.includes('/settings') || path.includes('/profile') || path.includes('/adminsettings')) {
    return <Outlet />
  }

  // 1. Guard /teacher routes: allow teachers/admins or users with academic permissions
  if (path.startsWith('/teacher')) {
    const isTeacherOrAdmin = normalizedRole === 'teacher' || normalizedRole === 'staff' || isAdminRole || storeAuth.role === 'teacher' || storeAuth.role === 'STAFF'
    const hasAcademicPerm =
      storeAuth.hasPermission('academics.view') ||
      storeAuth.hasPermission('academics:view') ||
      storeAuth.hasPermission('sections.view') ||
      storeAuth.hasPermission('sections:view') ||
      storeAuth.hasPermission('grades.view') ||
      storeAuth.hasPermission('grades:view') ||
      storeAuth.hasPermission('class-management:view') ||
      storeAuth.hasPermission('class-management.view') ||
      storeAuth.hasPermission('students.view') ||
      storeAuth.hasPermission('assignments.view') ||
      storeAuth.hasPermission('attendance.view') ||
      storeAuth.hasPermission('profile:view') ||
      storeAuth.hasPermission('teacher-profile:view')

    if (!isTeacherOrAdmin && !hasAcademicPerm) {
      return <Navigate to={getStoredRoleHomePath()} replace />
    }
  }

  // 2. Guard restricted /admin routes unless user is admin or has permission
  const hasGlobalWildcard = storeAuth.hasPermission('*')
  if (!isAdminRole && !hasGlobalWildcard) {
    if (path === '/admin/dashboard' || path === '/admin' || path === '/admin/') {
      const homePath = getStoredRoleHomePath()
      if (homePath !== path) {
        return <Navigate to={homePath} replace />
      }
    }

    const matchedRule = ROUTE_PERMISSION_MAP.find((rule) => path.startsWith(rule.prefix))
    if (matchedRule) {
      const canAccess = matchedRule.permissions.some((perm) => storeAuth.hasPermission(perm))
      if (!canAccess) {
        const homePath = getStoredRoleHomePath()
        if (homePath !== path) {
          return <Navigate to={homePath} replace />
        }
      }
    } else if (path.startsWith('/admin')) {
      // Fallback for unlisted /admin sub-routes when non-admin accesses them
      const modulePrefix = path.split('/')[2] || ''
      const canAccessModule = modulePrefix ? storeAuth.hasPermission(modulePrefix) : false
      if (!canAccessModule) {
        const homePath = getStoredRoleHomePath()
        if (homePath !== path) {
          return <Navigate to={homePath} replace />
        }
      }
    }
  }

  // 3. Guard general non-prefixed routes for teacher role
  if (normalizedRole === 'teacher' && !path.startsWith('/teacher')) {
    if (path.startsWith('/finance') || path === '/finance') {
      if (!storeAuth.hasPermission('finance.view') && !storeAuth.hasPermission('finance')) {
        return <Navigate to="/teacher/dashboard" replace />
      }
    }
  }

  return <Outlet />
}
